/**
 * The port every backend implements.
 *
 * Two operations: read everything at open, and apply one atomic patch per
 * commit. Nothing else — no cursors, no partial loads, no interactive
 * transaction. That is the largest contract IndexedDB, an HTTP endpoint and
 * Postgres can *all* honour, and the engine above it is written against exactly
 * this so swapping backends touches no caller.
 *
 * Everything crossing this line is plain JSON. `Map` becomes an entry array and
 * a branded id becomes a string, because IndexedDB's structured clone and an
 * HTTP body agree on that and on nothing wider.
 */

import type { Role, RoleKind } from '@morph/spec';
import type { PropertySlot } from './model.js';
import type { Value } from './value.js';

export interface PersistedObject {
  readonly id: string;
  readonly revision: number;
  readonly typeHint?: string;
  readonly slots: readonly (readonly [string, PropertySlot])[];
  readonly deletedAt?: string;
}

export interface PersistedKey {
  readonly id: string;
  readonly label: string;
  readonly kind: RoleKind;
  readonly roleHint?: Role;
  readonly createdAt: string;
  readonly retiredAt?: string;
}

export interface PersistedEdge {
  readonly id: string;
  readonly from: string;
  /** The private key, absent in records written before edges had one. Such an
   *  edge reads through `via` and cannot be mutated — see `EdgeRecord`. */
  readonly key?: string;
  readonly via: Role;
  readonly to: string;
  readonly anchor?: string;
  readonly at: string;
}

export interface PersistedManifest {
  readonly ref: string;
  readonly entries: readonly { readonly role: Role; readonly key: string }[];
}

/** What one commit changed about one object, so `undo` can put back exactly
 *  that and nothing else.
 *
 *  Without it undo restored whole-object snapshots, which reverted every LATER
 *  write to the same object — including a property added afterwards, deleted
 *  outright. That is the data loss the demo's Ctrl+Z could reach. */
export interface RestoreScope {
  readonly id: string;
  /** The property keys the commit wrote. A key absent from the snapshot is one
   *  the commit CREATED, so restoring it means removing the slot again. */
  readonly keys: readonly string[];
  /** The commit changed the object's tombstone. Symmetric on purpose: it is
   *  read as "take `deletedAt` from the snapshot", which retires on the way back
   *  as readily as it revives. */
  readonly lifecycle?: boolean;
}

/** What `undo` restores from. A snapshot rather than a list of inverse
 *  operations: the mutation vocabulary has no way to say "make this slot absent
 *  again", so an operation log cannot undo a write that created a slot. A
 *  snapshot can, and 0007 requires one undo stack across both doors. */
export interface RestorePatch {
  readonly objects: readonly PersistedObject[];
  readonly absentObjects: readonly string[];
  readonly keys: readonly PersistedKey[];
  readonly absentKeys: readonly string[];
  readonly edges: readonly PersistedEdge[];
  readonly absentEdges: readonly string[];
  /** Per object, what the commit actually touched. Absent on a journal written
   *  before undo was scoped — those still restore whole objects, because that is
   *  all they recorded. */
  readonly scope?: readonly RestoreScope[];
  /**
   * The manifest the edit was made AGAINST, when it published a role.
   *
   * @Codex, P2 on `ef26359`: *"Undo subsequently removes the newly defined key
   * via `absentKeys` while leaving the scope on the new manifest, so that
   * manifest permanently points at a nonexistent key; later reads cannot
   * resolve the role and later writes can keep selecting the deleted cached key
   * and be refused."*
   *
   * `0126` made minting a key, setting it and publishing its role one commit.
   * The inverse has to be one commit too, and the key half was already there —
   * `absentKeys` — while the manifest half was not, so undo half-reverted into
   * a state neither edit had ever produced.
   *
   * Optional for the same reason `scope` is: a journal written before this
   * existed cannot supply it, and undo of such a record leaves the manifest
   * where it is rather than guessing which one preceded it.
   */
  readonly manifest?: string;
}

/** Enough to rebuild the public `Instruction[]` inverse without persisting a
 *  `Grant` — grants carry a symbol brand, which no serializer preserves, so
 *  storing one would produce an unbranded object that only looked like a grant. */
export interface InverseStep {
  readonly object: string;
  readonly key: string;
  readonly role: Role;
  readonly mutation: 'set-value' | 'set-enum' | 'set-date' | 'toggle';
  readonly before: Value;
  readonly revision: number;
}

export interface PersistedJournal {
  readonly id: string;
  readonly at: string;
  readonly via: 'binding' | 'editor';
  readonly specHash?: string;
  readonly touched: readonly string[];
  readonly steps: readonly InverseStep[] | null;
  readonly restore: RestorePatch;
  /** The manifest this commit PUBLISHED, when it published one. `restore.manifest`
   *  is the one it replaced; undo needs both ends to be symmetric. Absent on
   *  every record written before it existed, and a redo of one of those behaves
   *  exactly as it did. */
  readonly publishedManifest?: string;
}

export interface PersistedSnapshot {
  readonly objects: readonly PersistedObject[];
  readonly keys: readonly PersistedKey[];
  readonly edges: readonly PersistedEdge[];
  readonly manifests: readonly PersistedManifest[];
  readonly journal: readonly PersistedJournal[];
  /**
   * The store-wide commit revision this load is consistent with.
   *
   * Absent in a database written before it existed, which reads as 0 — the same
   * value a new database starts at, so the first conditional commit against it
   * succeeds and the counter begins.
   */
  readonly revision?: number;
}

export interface PersistPatch {
  readonly objects?: readonly PersistedObject[];
  readonly deletedObjects?: readonly string[];
  readonly keys?: readonly PersistedKey[];
  readonly deletedKeys?: readonly string[];
  readonly edges?: readonly PersistedEdge[];
  readonly deletedEdges?: readonly string[];
  readonly manifests?: readonly PersistedManifest[];
  readonly journal?: readonly PersistedJournal[];
}

/**
 * What one commit expects to be true, and what it makes true.
 *
 * Decision 0027: comparison, persistence of every fact in the batch, and
 * advancement of the store-wide revision are ONE atomic backend operation. Not
 * an engine-local check — an engine's own view is exactly what goes stale when
 * a second tab commits, so a check against it passes at the moment it is most
 * wrong.
 */
export interface CommitBasis {
  /** The store-wide revision the batch was planned against. */
  readonly expect: number;
  /** What it becomes. Advanced in the same transaction that writes the batch. */
  readonly next: number;
}

/** Applied, or refused because the basis had moved — never silently merged. */
export type CommitOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly current: number };

export interface StorePersistence {
  load(): Promise<PersistedSnapshot>;
  /**
   * Conditional on `basis`. A backend that cannot compare and advance in the
   * same atomic write as the batch cannot implement this store — decision 0027
   * says so in those terms, and the reason is that per-engine queues and grant
   * revisions are not a boundary two tabs share.
   */
  commit(patch: PersistPatch, basis: CommitBasis): Promise<CommitOutcome>;
  close(): Promise<void>;
}

export const EMPTY_SNAPSHOT: PersistedSnapshot = {
  objects: [], keys: [], edges: [], manifests: [], journal: [], revision: 0,
};

/**
 * Nothing survives the tab. The in-memory store is not a lesser backend — it
 * is the one the tests run against, so it has to be the same engine with this
 * underneath it rather than a second implementation that can disagree.
 *
 * Which is exactly why it compares. This returned `{ ok: true }` unconditionally
 * on the reasoning that *"nothing is shared with it, so nothing can have moved
 * under it"* — true of two stores, false of one store with two writes in
 * flight. @Codex, P1 on #164: two overlapping `edit` calls on one memory engine
 * both read the same revision, both reach an already-resolved commit, and both
 * were accepted. The IndexedDB backend refuses the second. A memory backend
 * that disagrees with the real one is the second implementation this comment
 * says it must not be, and it is the one every in-memory suite runs against.
 *
 * Per store, not shared: a module-level counter would make two independent
 * memory stores refuse each other's writes.
 */
export function createMemoryPersistence(): StorePersistence {
  let revision = 0;
  /**
   * IT HAS TO RETAIN THE BATCHES, and finding out why cost a red test.
   *
   * This returned `EMPTY_SNAPSHOT` at first, on the reasoning that nothing
   * survives the tab so there is nothing to load. That was true only while the
   * memory backend accepted every commit. Once it started refusing a stale one,
   * the engine's refusal path does a REPLACEMENT refresh — clear the maps, load
   * from the backend — and a backend with nothing in it emptied the store.
   *
   * Measured as `manifest "mf_1sotmkh" is not known to this store`, on the very
   * next read after a refused binding write. A backend that cannot answer
   * `load` with what it accepted cannot honour the contract it claims to
   * implement; the two halves are one obligation, not two features.
   */
  const objects = new Map<string, PersistedObject>();
  const keys = new Map<string, PersistedKey>();
  const edges = new Map<string, PersistedEdge>();
  const manifests = new Map<string, PersistedManifest>();
  const journal = new Map<string, PersistedJournal>();
  return {
    load: async () => ({
      objects: [...objects.values()], keys: [...keys.values()],
      edges: [...edges.values()], manifests: [...manifests.values()],
      journal: [...journal.values()], revision,
    }),
    commit: async (patch, basis) => {
      if (revision !== basis.expect) return { ok: false, current: revision };
      for (const o of patch.objects ?? []) objects.set(o.id, o);
      for (const id of patch.deletedObjects ?? []) objects.delete(id);
      for (const k of patch.keys ?? []) keys.set(k.id, k);
      for (const id of patch.deletedKeys ?? []) keys.delete(id);
      for (const e of patch.edges ?? []) edges.set(e.id, e);
      for (const id of patch.deletedEdges ?? []) edges.delete(id);
      for (const m of patch.manifests ?? []) manifests.set(m.ref, m);
      for (const j of patch.journal ?? []) journal.set(j.id, j);
      revision = basis.next;
      return { ok: true };
    },
    close: async () => {},
  };
}

/** @deprecated One shared instance cannot hold a revision. Use
 *  `createMemoryPersistence()`, which is what `createMemoryStore` does. */
export const nullPersistence: StorePersistence = {
  load: async () => EMPTY_SNAPSHOT,
  commit: async () => ({ ok: true }),
  close: async () => {},
};
